import PropTypes from "prop-types";
import { useEffect } from "react";
import CustomerImageUploadShared from "../../../../components/CustomerImageUpload";
import { inputValidation } from "../../../../util";
import {
  allowNotes,
  allowPersonalization,
  getAspectRatio,
  isCustomImageUpload,
  isDigitalSignature,
  isGiftPrint,
  isPresigned,
  isUnsigned,
} from "./productAdapter";
import s from "./ProductForm.module.css";
import {
  updateCustomerImage,
  updateNotes,
  updatePersonalizationTo,
  updateRecipientEmail,
  useProductPageContext,
} from "./state";

ProductForm.propTypes = {
  product: PropTypes.object.isRequired,
};

export default function ProductForm({ product }) {
  const [state, dispatch] = useProductPageContext();

  const showNotesField = allowNotes(product);
  const notesLabel = "Special Instructions (optional)";

  const canPersonalize = allowPersonalization(product);

  useEffect(() => {
    if (!canPersonalize) {
      dispatch(updatePersonalizationTo(""));
    }
  }, [canPersonalize, dispatch]);

  const showCustomUpload = isCustomImageUpload(product);
  const needsImage = showCustomUpload && !state.customerImage;

  return (
    <div className={s.formSection}>
      {/* Customer image upload */}
      {showCustomUpload && (
        <div className={s.fieldGroup}>
          <CustomerImageUploadShared
            aspectRatio={getAspectRatio(product)}
            onUploadComplete={(image) => dispatch(updateCustomerImage(image))}
            hasUploadedImage={Boolean(state.customerImage)}
            onChangeImage={() => dispatch(updateCustomerImage(null))}
            showTerms
          />
          {needsImage && (
            <p className={s.infoMsg} role="status">
              Please upload an image to continue
            </p>
          )}
        </div>
      )}

      {/* Make it out to */}
      {allowPersonalization(product) && (
        <div className={s.fieldGroup}>
          <div className={s.fieldLabelRow}>
            <label className={s.fieldLabel} htmlFor="pm-personalization-to">
              Make it out to (optional)
            </label>
            {state.personalizationTo.length > 0 && (
              <span className={s.fieldCount}>
                {state.personalizationTo.length} /{" "}
                {inputValidation.personalizationTo.maxLength}
              </span>
            )}
          </div>
          <input
            id="pm-personalization-to"
            className={s.fieldInput}
            type="text"
            value={state.personalizationTo}
            onChange={(e) => {
              if (
                e.target.value.length >
                inputValidation.personalizationTo.maxLength
              ) {
                return;
              }
              dispatch(updatePersonalizationTo(e.target.value));
            }}
            maxLength={inputValidation.personalizationTo.maxLength}
            placeholder="Enter your name..."
          />
        </div>
      )}

      {/* Gift print recipient email */}
      {isGiftPrint(product) && (
        <div className={s.fieldGroup}>
          <div className={s.fieldLabelRow}>
            <label className={s.fieldLabel} htmlFor="pm-recipient-email">
              Recipient&apos;s Email
            </label>
            {state.recipientEmail.length > 0 && (
              <span className={s.fieldCount}>
                {state.recipientEmail.length} / 50
              </span>
            )}
          </div>
          <input
            id="pm-recipient-email"
            className={s.fieldInput}
            type="email"
            value={state.recipientEmail}
            onChange={(e) => {
              if (e.target.value.length > 50) return;
              dispatch(updateRecipientEmail(e.target.value.trimStart()));
            }}
            maxLength={50}
            placeholder="Enter recipient's email..."
            required
          />
        </div>
      )}

      {/* Notes field — shows as "Special Instructions" */}
      {showNotesField && (
        <div className={s.fieldGroup}>
          <div className={s.fieldLabelRow}>
            <label className={s.fieldLabel} htmlFor="pm-notes">
              {notesLabel}
            </label>
            {state.notes.length > 0 && (
              <span className={s.fieldCount}>
                {state.notes.length} / {inputValidation.notes.maxLength}
              </span>
            )}
          </div>
          <textarea
            id="pm-notes"
            className={s.fieldTextarea}
            value={state.notes}
            onChange={(e) => {
              if (e.target.value.length > inputValidation.notes.maxLength) {
                return;
              }
              dispatch(updateNotes(e.target.value));
            }}
            maxLength={inputValidation.notes.maxLength}
            rows={3}
          />
        </div>
      )}

      {/* Hints */}
      {isDigitalSignature(product) && (
        <p className={s.formHint}>
          While the signer(s) will be able to read the custom message you
          provide here, this product cannot be personalized with a name or
          quote, because it is signed digitally.
        </p>
      )}

      {allowNotes(product) && (isPresigned(product) || isUnsigned(product)) && (
        <p className={s.formHint}>
          Please note: The signer(s) will not be able to read the message you
          provide here, as this product
          {isPresigned(product) ? " has already been signed." : " is unsigned."}
        </p>
      )}
    </div>
  );
}
